import { ReactNode } from 'react';
import styled from 'styled-components';
import { colors } from '@karnott/colors';
import { pixelSize, zIndex } from '@karnott/theme';

type Orientation = 'top' | 'bottom' | 'left' | 'right';

export type TooltipMessageProps = {
  /** Content of the tooltip */
  message?: ReactNode;
  /** Sets the orientation of the message */
  orientation?: Orientation;
  /** Prevents the message from wrapping */
  noWrap?: boolean;
  /** Shows a tooltip with a smaller font size */
  small?: boolean;
  /** Don’t apply any style to the content of the tooltip */
  unstyledContent?: boolean;
};

/** Tooltip component alone */
export function TooltipMessage({
  message,
  orientation = 'top',
  noWrap = false,
  small = false,
  managedPositioning = false,
}: TooltipMessageProps & { managedPositioning?: boolean }) {
  return (
    <TooltipMessageContainer $orientation={orientation} managedPositioning={managedPositioning}>
      <Message $noWrap={noWrap} small={small}>
        {message}
      </Message>
    </TooltipMessageContainer>
  );
}

const TooltipMessageContainer = styled.div<{
  $orientation: Orientation;
  managedPositioning: boolean;
}>`
  position: ${({ managedPositioning }) => (managedPositioning ? 'unset' : 'absolute')};
  display: flex;
  width: ${({ $orientation, managedPositioning }) =>
    managedPositioning ? 'min-content' : $orientation === 'top' || $orientation === 'bottom' ? '100%' : 'auto'};
  height: ${({ $orientation }) => ($orientation === 'left' || $orientation === 'right' ? '100%' : 'auto')};
  left: ${({ $orientation, managedPositioning }) =>
    managedPositioning ? 'unset' : $orientation === 'left' ? 'inherit' : '0px'};
  align-items: flex-start;
  justify-content: center;
  z-index: ${zIndex('sky')};
  pointer-events: none;

  ${({ $orientation, managedPositioning }) => {
    if (managedPositioning) {
      return null;
    }
    switch ($orientation) {
      case 'top':
        return 'bottom: calc(100% + 3px);';
      case 'bottom':
        return 'top: calc(100% + 3px);';
      case 'left':
        return 'right: calc(100% + 12px);';
      case 'right':
        return 'left: calc(100% + 12px);';
    }
  }}
`;

const Message = styled.span<{
  $noWrap: boolean;
  small: boolean;
}>`
  position: relative;
  color: ${colors('white')};
  background-color: ${colors('black')};
  border-radius: 4px;
  border-color: ${colors('white')};
  padding: 3px ${({ small }) => (small ? 5 : 6)}px;
  white-space: ${({ $noWrap }) => ($noWrap ? 'nowrap' : 'pre-line')};
  display: flex;
  width: auto;
  font-size: ${({ small }) => (small ? '10px' : pixelSize('small'))};
  text-align: left;
  font-weight: normal;
`;
